import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export type SaleOperationType = 'SALE' | 'COMMISSION' | 'PROCESSING' | 'EXPORT';

export interface GoodsSaleItem {
  service_description?: string | null;
  /** 0386: склад рядка (null = склад шапки) — мультисклад одним документом. */
  warehouse_id?: string | null;
  /** Вкладка «Рахунки обліку»: перебиття рахунків рядка (null = авто). */
  account_code?: string | null;
  revenue_account_code?: string | null;
  cost_account_code?: string | null;
  id?: string;
  line_number: number;
  /** 0445: рядок-ОЗ — nomenclature_id порожній, fixed_asset_id заданий (рівно одне). */
  nomenclature_id: string | null;
  fixed_asset_id?: string | null;
  fa_primary_cost_snapshot?: string | null;
  fa_accumulated_depreciation_snapshot?: string | null;
  characteristic_id?: string | null;
  series_id?: string | null;
  quantity: string;
  uom_id?: string | null;
  price: string;
  // П2 (2026-07-23): знижка рядка (ручна або з договору).
  discount_percent?: string;
  discount_amount?: string;
  amount_without_vat: string;
  vat_rate_id: string;
  vat_amount: string;
  total_amount: string;
  cost_amount?: string;
  project_id?: string | null;
  // TD-B-P5.5.3 (2026-06-01): tax_purpose для пропорційного ПДВ.
  // null → unclassified, FK на ref.tax_purposes.id (TAXABLE/EXEMPT/тощо).
  tax_purpose_id?: string | null;
}

// TD-B-P5.5.3: довідник tax_purposes для UI dropdown.
export type TaxPurposeCode =
  | 'TAXABLE'
  | 'EXEMPT'
  | 'EXPORT'
  | 'NON_BUSINESS'
  | 'NOT_OBJECT';

export interface TaxPurpose {
  id: string;
  code: TaxPurposeCode;
  name_uk: string;
  description: string | null;
}

export const TAX_PURPOSE_LABELS: Record<TaxPurposeCode, string> = {
  TAXABLE: 'Оподатковується ПДВ',
  EXEMPT: 'Звільнено від ПДВ (ст.197)',
  EXPORT: 'Експорт (0% ПДВ)',
  NON_BUSINESS: 'Не господарська діяльність',
  NOT_OBJECT: 'Не є об\'єктом ПДВ (ст.196)',
};

/** 0445: вид документа продажу за рядками. */
export type DocKind = 'GOODS' | 'SERVICE' | 'EQUIPMENT' | 'FIXED_ASSET';
export const DOC_KIND_UA: Record<DocKind, string> = {
  GOODS: 'товари', SERVICE: 'послуги', EQUIPMENT: 'обладнання', FIXED_ASSET: 'ОЗ',
};

export interface GoodsSaleListItem {
  currency_code?: string | null;
  doc_kind?: DocKind | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  warehouse_id: string | null;  // NULL: документ лише послуг
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GoodsSale extends GoodsSaleListItem {
  currency_code?: string | null;
  operation_type: SaleOperationType;
  contract_id: string;
  customer_order_id: string | null;
  advance_offset_mode?: 'AUTO' | 'NONE';
  prices_include_vat: boolean;
  // ч.1374: «Враховувати ПДВ» / «Авторозрахунок ПДВ» (1С).
  vat_considered: boolean;
  vat_auto: boolean;
  allow_overshipment: boolean;
  delivery_address: string | null;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: GoodsSaleItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GoodsSaleCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_type?: SaleOperationType;
  contractor_id: string;
  /** 2026-06-02: nullable — backend auto-create default contract. */
  contract_id: string | null;
  warehouse_id: string | null;  // NULL: документ лише послуг
  customer_order_id?: string | null;
  advance_offset_mode?: 'AUTO' | 'NONE';
  prices_include_vat?: boolean;
  vat_considered?: boolean;
  vat_auto?: boolean;
  allow_overshipment?: boolean;
  delivery_address?: string | null;
  comment?: string | null;
  items: GoodsSaleItem[];
  project_id?: string | null;
}

export type GoodsSaleUpdate = Partial<GoodsSaleCreate>;

export const goodsSales = {
  list: (params?: { organization_id?: string; contractor_id?: string; nomenclature_id?: string; posted?: boolean; doc_kind?: DocKind; limit?: number;
    offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc';
    date_from?: string;
    date_to?: string; }) =>
    api.get<Page<GoodsSaleListItem>>('/goods-sales', { params }).then((r) => r.data),
  get: (id: string) => api.get<GoodsSale>(`/goods-sales/${id}`).then((r) => r.data),
  // 2026-07-27: реальні рахунки, які підбере проведення (вкладка «Рахунки обліку»).
  previewAccounts: (p: {
    organization_id: string;
    items: Array<{ nomenclature_id?: string | null; fixed_asset_id?: string | null; warehouse_id?: string | null }>;
  }) => api.post<Array<{
    nomenclature_id: string | null;
    fixed_asset_id?: string | null;
    account_code: string | null;
    revenue_account_code: string;
    cost_account_code: string | null;
  }>>('/goods-sales/preview-accounts', p).then((r) => r.data),
  create: (payload: GoodsSaleCreate) =>
    api.post<GoodsSale>('/goods-sales', payload).then((r) => r.data),
  update: (id: string, payload: GoodsSaleUpdate) =>
    api.patch<GoodsSale>(`/goods-sales/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/goods-sales/${id}`).then(() => undefined),
  post: (id: string) => api.post<GoodsSale>(`/goods-sales/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<GoodsSale>(`/goods-sales/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-sales/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
